import { EventEmitter, Injectable } from '@angular/core';
import { AngularFirestore, CollectionReference, DocumentReference } from '@angular/fire/compat/firestore';
import firebase from 'firebase/compat/app';
import { GameResult, Transaction, WinningResult } from '@app/@shared/models/transaction.model';
import { Customer } from './klaviyo.service';
import { Observable, of, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TransactionService {
  private collection: CollectionReference<Transaction>;
  private collectionResults: CollectionReference<any>;
  allowedProducts = ['Tris', 'Melate', 'MelateRetro', 'Chispazo', 'GanaGato'];
  refresh = new EventEmitter<boolean>();
  cachedWinningResults: GameResult[] | null = null;

  constructor(private firestore: AngularFirestore) {
    this.collection = this.firestore.collection<Transaction>('transactions').ref;
    this.collectionResults = this.firestore.collection<any>('results').ref;
  }

  async getAll(): Promise<Transaction[]> {
    const snap = await this.collection.get();
    const data = snap.docs.map((o) => {
      return { uid: o.id, ...o.data() };
    });
    return data;
  }
  async getById(uid: string): Promise<Transaction | null> {
    const snap = await this.collection.doc(uid).get();
    if (snap.exists) {
      const data = { uid: snap.id, ...snap.data() } as Transaction;
      if (typeof data.data === 'string') {
        data.data = JSON.parse(data.data);
      }
      return data;
    } else {
      return null;
    }
  }
  async getByCustomerId(customerId: string, status: string, limit: number): Promise<Transaction[]> {
    const ref = this.getRef('customers', customerId);
    const snap = await this.collection
      .where('customerId', '==', ref)
      .where('status', '==', status)
      .where('data', '!=', null)
      .orderBy('createdAt', 'desc')
      .limit(limit)
      .get();
    if (snap.size) {
      return snap.docs.map((d) => {
        const data = { uid: d.id, ...d.data() } as Transaction;
        if (typeof data.data === 'string') data.data = JSON.parse(data.data);
        return data;
      });
    } else {
      return [];
    }
  }

  async getByCustomerIdAndRange(customerId: string, start: Date, end: Date): Promise<Transaction[]> {
    const ref = this.firestore.collection('customers').doc(customerId).ref;
    const snap = await this.collection
      .where('customerId', '==', ref)
      .where('data', '!=', null)
      .where('createdAt', '>=', start)
      .where('createdAt', '<=', end)
      .get();
    if (snap.size) {
      return snap.docs.map((d) => {
        const data = { uid: d.id, ...d.data() } as Transaction;
        if (typeof data.data === 'string') data.data = JSON.parse(data.data);
        return data;
      });
    } else {
      return [];
    }
  }

  async getTrisByCustomerId(customerId: string, drawNumber: string): Promise<Transaction[]> {
    const ref = this.getRef('customers', customerId);
    const snap = await this.collection
      .where('customerId', '==', ref)
      .where('status', '==', 'completed')
      .where('type', '==', 'wallet-purchase-Tris')
      .where('drawNumbers', 'array-contains', drawNumber)
      .get();
    if (snap.size) {
      return snap.docs.map((d) => {
        const data = { uid: d.id, ...d.data() } as Transaction;
        if (typeof data.data === 'string') data.data = JSON.parse(data.data);
        return data;
      });
    } else {
      return [];
    }
  }

  /*   async getTransaction(customerId: string): Promise<any[]> {
    const ref = this.firestore.collection('customers').doc(customerId).ref;
    const snap = await this.collection
      .where('customerId', '==', ref)
      .where('status', '==', 'success')
      .limit(60)
      .orderBy('createdAt', 'desc')
      .get();

    const transactions = await Promise.all(
      snap.docs.map(async (doc) => {
        const transactionData = doc.data() as Transaction;

        const customerSnap = await this.firestore.doc(transactionData.customerId.path).get().toPromise();
        const customerData = customerSnap?.data() ?? null;

        return {
          uid: doc.id,
          ...transactionData,
          customer: customerData,
        };
      })
    );

    return transactions;
  } */

  async getTransaction(customerId: string): Promise<any[]> {
    const ref = this.firestore.collection('customers').doc(customerId).ref;

    const now = new Date();
    const sixtyDaysAgo = new Date(now);
    sixtyDaysAgo.setDate(now.getDate() - 60);
    sixtyDaysAgo.setHours(0, 0, 0, 0);

    const sixtyDaysAgoTimestamp = firebase.firestore.Timestamp.fromDate(sixtyDaysAgo);

    const snap = await this.collection
      .where('customerId', '==', ref)
      .where('status', '==', 'completed')
      .where('createdAt', '>=', sixtyDaysAgoTimestamp)
      .orderBy('createdAt', 'desc')
      .get();

    return snap.docs.map((doc) => {
      const transactionData = doc.data() as Transaction;

      return {
        uid: doc.id,
        ...transactionData,
        createdAt:
          transactionData.createdAt && 'seconds' in transactionData.createdAt
            ? new Date(transactionData.createdAt.seconds * 1000)
            : null,
        data:
          typeof transactionData.data === 'string'
            ? (JSON.parse(transactionData.data) as Transaction)
            : transactionData.data,
      };
    });
  }

  // Crear una nueva transacción
  async create(item: Transaction): Promise<DocumentReference<Transaction>> {
    if (typeof item.data !== 'string') {
      item.data = JSON.stringify(item.data);
    }
    return await this.collection.add(item);
  }

  // Actualizar un elemento
  async update(uid: string, item: Partial<Transaction>): Promise<void> {
    if (item.data && typeof item.data !== 'string') {
      item.data = JSON.stringify(item.data);
    }
    await this.collection.doc(uid).update(item);
  }

  // Eliminar un elemento
  async delete(uid: string): Promise<void> {
    await this.collection.doc(uid).delete();
  }

  subscribe(transactionId: string) {
    return this.firestore.collection<Transaction>('transactions').doc(transactionId).valueChanges({
      idField: 'id',
    });
  }

  getRef(collection: string, id: string) {
    return this.firestore.collection(collection).doc(id).ref;
  }

  async getTransactionTradicionalFromDrawNumber(drawNumber: number) {
    const snapt = await this.collection.where('prizeDraws', 'array-contains', drawNumber).get();
    return snapt.docs.map((d) => {
      return {
        ...d.data(),
        uid: d.id,
      } as Transaction;
    });
  }

  async getTransactionAddFundsLimit(customerId: string) {
    const monthAgo = new Date();
    monthAgo.setMonth(monthAgo.getMonth() - 1);

    const ref = this.firestore.collection<Customer>('customers').doc(customerId).ref;
    const snapt = await this.collection
      .where('customerId', '==', ref)
      .where('type', '==', 'wallet_recharge_user')
      .where('createdAt', '>=', monthAgo)
      .where('data.save.method', '==', 'cc_card')
      .get();
    return snapt.docs.map((d) => {
      return {
        ...d.data(),
        uid: d.id,
      } as Transaction;
    });
  }

  // confirmado
  getTransactionWinning(): Observable<WinningResult[]> {
    return this.firestore
      .collection<WinningResult>('results', (ref) => ref.where('product', 'in', this.allowedProducts))
      .valueChanges();
  }

  getTransactionWinningR(): Observable<GameResult[]> {
    const localKey = 'cachedWinningResults';

    // Verifica si ya hay cache local y si no ha expirado
    const cachedRaw = localStorage.getItem(localKey);
    if (cachedRaw) {
      try {
        const cached = JSON.parse(cachedRaw);
        const now = Date.now();
        const cacheTime = cached.timestamp || 0;
        const expirationMs = 40 * 60 * 1000; // 40 minutos

        if (now - cacheTime < expirationMs) {
          // console.log('usando cache de resultados');
          return of(cached.data);
        } else {
          // console.log('cache expirado, eliminando cache');
          localStorage.removeItem(localKey);
        }
      } catch (e) {
        console.error('Error al parsear cache local:', e);
        localStorage.removeItem(localKey); // Borra el cache si está corrupto
      }
    }

    // Si no hay cache válido, hace la consulta a Firestore
    //console.log('consultando resultados desde firestore');
    return this.firestore
      .collection<GameResult>('gameResult', (ref) => ref.where('product', 'in', this.allowedProducts))
      .valueChanges()
      .pipe(
        tap((results) => {
          try {
            localStorage.setItem(
              localKey,
              JSON.stringify({
                timestamp: Date.now(),
                data: results,
              })
            );
          } catch (e) {
            console.error('No se pudo guardar en localStorage:', e);
          }
        })
      );
  }

  clearWinningResultsCache(): void {
    localStorage.removeItem('cachedWinningResults');
  }
}
